/**
 * Utilitaire Web Push — gestion de l'abonnement PWA.
 *
 * Usage :
 *   import { subscribePush, unsubscribePush, isPushSupported } from './pushNotifications';
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Vérifie si le navigateur supporte les notifications push */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** Retourne l'état de permission actuel */
export function getPushPermission() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

/** Convertit une clé base64url en Uint8Array (pour applicationServerKey) */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

/** Récupère la clé publique VAPID depuis le backend */
async function fetchVapidPublicKey() {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_URL}/api/push/vapid-public-key`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Impossible de récupérer la clé VAPID');
  const { public_key } = await res.json();
  return public_key;
}

/** Enregistre la souscription push auprès du backend */
async function savePushSubscription(sub) {
  const token = localStorage.getItem('token');
  const json  = sub.toJSON();
  await fetch(`${API_URL}/api/push/subscribe`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body:    JSON.stringify({ endpoint: json.endpoint, keys: json.keys }),
  });
}

/** Supprime la souscription push du backend */
async function deletePushSubscription(sub) {
  const token = localStorage.getItem('token');
  const json  = sub.toJSON();
  await fetch(`${API_URL}/api/push/unsubscribe`, {
    method:  'DELETE',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body:    JSON.stringify({ endpoint: json.endpoint, keys: json.keys }),
  });
}

/**
 * Demande la permission et abonne l'utilisateur aux notifications push.
 * @returns {Promise<'granted'|'denied'|'unsupported'>}
 */
export async function subscribePush() {
  if (!isPushSupported()) return 'unsupported';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission;

  try {
    const reg        = await navigator.serviceWorker.ready;
    const vapidKey   = await fetchVapidPublicKey();
    const appKey     = urlBase64ToUint8Array(vapidKey);

    // Récupère ou crée la souscription
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly:      true,
        applicationServerKey: appKey,
      });
    }

    await savePushSubscription(sub);
    localStorage.setItem('push_subscribed', 'true');
    return 'granted';
  } catch (err) {
    console.warn('[Push] Erreur souscription:', err);
    return 'error';
  }
}

/**
 * Désabonne l'utilisateur des notifications push.
 */
export async function unsubscribePush() {
  if (!isPushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await deletePushSubscription(sub);
      await sub.unsubscribe();
    }
    localStorage.removeItem('push_subscribed');
  } catch (err) {
    console.warn('[Push] Erreur désabonnement:', err);
  }
}

/**
 * Vérifie si l'utilisateur est déjà abonné.
 */
export async function isPushSubscribed() {
  if (!isPushSupported()) return false;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  return !!sub;
}

/**
 * À appeler après le login : propose les notifs si pas encore répondu.
 * Sur iOS, ne rien faire automatiquement (nécessite un geste).
 */
export async function askPushOnLogin() {
  if (!isPushSupported()) return;
  if (getPushPermission() === 'denied') return;

  // Déjà accepté et déjà abonné → re-synchro silencieuse
  if (getPushPermission() === 'granted') {
    await subscribePush();
    return;
  }

  // iOS : ne pas demander automatiquement (must be user gesture)
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (isIOS) return;

  // Android Chrome / Desktop : demander après un court délai
  // On mémorise qu'on a déjà demandé pour ne pas re-demander à chaque login
  const alreadyAsked = localStorage.getItem('push_asked');
  if (alreadyAsked) return;

  localStorage.setItem('push_asked', 'true');
  // Petit délai pour ne pas interrompre le chargement
  setTimeout(() => subscribePush(), 2500);
}
